import React, { useState } from 'react';
import { CheckCircle, MessageSquare, Filter } from 'lucide-react';
import { useFeedbacks } from '../hooks';
import { releaseFeedback } from '../api';
import { FeedbackDetailModal } from '../components/FeedbackDetailModal';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { formatBrasilia } from '../../../shared/lib/date';
import { useQueryClient } from '@tanstack/react-query';
import { FEEDBACKS_KEYS } from '../api';
import type { Feedback } from '../../../shared/lib/api';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'published', label: 'Aguardando Liberação' },
  { value: 'released', label: 'Liberados' },
];

const RhFeedbacksPage: React.FC = () => {
  const { data, isLoading, isError } = useFeedbacks();
  const feedbacks = data?.data ?? [];
  const queryClient = useQueryClient();

  const [filterManager, setFilterManager] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [detailFeedback, setDetailFeedback] = useState<Feedback | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const managerOptions = [
    { value: '', label: 'Todos os gestores' },
    ...Array.from(
      new Map(feedbacks.map(fb => [fb.managerId, fb.manager?.name ?? fb.managerId])).entries(),
    ).map(([id, name]) => ({ value: id, label: name })),
  ];

  const companyOptions = [
    { value: '', label: 'Todas as empresas' },
    ...Array.from(
      new Set(
        feedbacks
          .map(fb => (fb.collaborator as any)?.company?.tradeName)
          .filter(Boolean) as string[],
      ),
    ).map(name => ({ value: name, label: name })),
  ];

  const filtered = feedbacks.filter(fb => {
    if (filterManager && fb.managerId !== filterManager) return false;
    if (filterCompany && (fb.collaborator as any)?.company?.tradeName !== filterCompany) return false;
    if (filterStatus && fb.lifecycleStatus !== filterStatus) return false;
    return true;
  });

  const handleRelease = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await releaseFeedback(id);
      queryClient.invalidateQueries({ queryKey: FEEDBACKS_KEYS.all });
      setSuccessMessage('Feedback liberado com sucesso!');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch {
      setSuccessMessage('Erro ao liberar feedback.');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Revisão de Feedbacks</h1>
      </div>

      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          {successMessage}
        </div>
      )}

      {/* Filtros */}
      <div className="flex items-end gap-4 flex-wrap">
        <Filter className="w-5 h-5 text-gray-400 mb-3 flex-shrink-0" />
        <div className="w-56">
          <AutocompleteSelect
            label="Gestor"
            options={managerOptions}
            value={filterManager}
            onChange={setFilterManager}
          />
        </div>
        <div className="w-56">
          <AutocompleteSelect
            label="Empresa"
            options={companyOptions}
            value={filterCompany}
            onChange={setFilterCompany}
          />
        </div>
        <div className="w-56">
          <AutocompleteSelect
            label="Status"
            options={STATUS_OPTIONS}
            value={filterStatus}
            onChange={setFilterStatus}
          />
        </div>
      </div>

      {isLoading ? (
        <div>Carregando...</div>
      ) : isError ? (
        <div className="text-red-600">Erro ao carregar feedbacks.</div>
      ) : filtered.length === 0 ? (
        <div className="p-8 text-center bg-gray-50 rounded-xl border border-gray-200">
          <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Nenhum feedback encontrado.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Colaborador</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gestor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Período</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Publicado em</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ação</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.map(fb => (
                <tr
                  key={fb.id}
                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => setDetailFeedback(fb)}
                >
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {fb.collaborator?.name ?? fb.collaboratorId}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {fb.manager?.name ?? fb.managerId}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{fb.referencePeriod}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{formatBrasilia(fb.updatedAt)}</td>
                  <td className="px-6 py-4">
                    {fb.lifecycleStatus === 'published' ? (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">
                        Aguardando
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                        Liberado
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4" onClick={e => e.stopPropagation()}>
                    {fb.lifecycleStatus === 'published' && (
                      <button
                        onClick={e => handleRelease(fb.id, e)}
                        className="flex items-center gap-1 px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        <CheckCircle className="w-3 h-3" /> Liberar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detailFeedback && (
        <FeedbackDetailModal feedback={detailFeedback} onClose={() => setDetailFeedback(null)} />
      )}
    </div>
  );
};

export default RhFeedbacksPage;
